import React, {useMemo, useState, useRef, useCallback, useEffect} from "react";
import useZustandStore from "../../hooks/useZustandStore";

export function TextBlock({block, fontSize, lineHeight, onCharHover, onCharLeave}) {
    const showPinyin = useZustandStore((state) => state.settings.showPinyin);
    const charDataMap = useZustandStore((state) => state.charDataMap);
    const setActiveAnnotation = useZustandStore((state) => state.setActiveAnnotation);
    const [hoveredAnnIdx, setHoveredAnnIdx] = useState(null);
    const [popup, setPopup] = useState(null);
    const popupRef = useRef(null);

    // 字符位置 → 所属标注索引（-1 = 无标注）
    const annIndexByPos = useMemo(() => {
        const map = new Array(block.content.length).fill(-1);
        block.annotations?.forEach((a, idx) => {
            for (let i = a.start; i <= a.end; i++) map[i] = idx;
        });
        return map;
    }, [block.annotations, block.content.length]);

    const closePopup = useCallback(() => setPopup(null), []);

    const chars = [...block.content];

    return (
        <div className="mb-8">
            <p className={`text-ink text-justify tracking-[0.1em] indent-[2em] ${block.anim_class || "animate-fade-up"}`}
                style={{fontSize, lineHeight}}>
                {chars.map((char, j) => {
                    const annIdx = annIndexByPos[j];
                    const annotated = annIdx >= 0;
                    const hasChar = !!charDataMap[char]; // 有 chars 释义的字也能触发 tooltip
                    const isHovered = (annotated && annIdx === hoveredAnnIdx);
                    const isPopupActive = annotated && popup?.annIdx === annIdx;
                    const pinyin = showPinyin ? charDataMap[char]?.['Pinyin'] : null;

                    // 有 annotation 标注的字：带下划线 + 悬浮详情
                    const charNode = annotated ? (
                        <AnnotatedChar
                            key={j} char={char} isHovered={isHovered} isPopupActive={isPopupActive}
                            onMouseEnter={(e, rect) => {
                                setHoveredAnnIdx(annIdx);
                                onCharHover(char, e, rect);
                                setActiveAnnotation({ text: block.content.slice(block.annotations[annIdx].start, block.annotations[annIdx].end + 1), note: block.annotations[annIdx].note });
                            }}
                            onMouseMove={(e) => onCharHover(char, e, e.target.getBoundingClientRect())}
                            onMouseLeave={(e) => { setHoveredAnnIdx(null); onCharLeave(e); }}
                            onClick={(e, rect) => {
                                e.stopPropagation();
                                const ann = block.annotations[annIdx];
                                const txt = block.content.slice(ann.start, ann.end + 1);
                                setActiveAnnotation({ text: txt, note: ann.note });
                                onCharHover(char, e, rect);
                                setPopup(prev => prev?.annIdx === annIdx ? null : { annIdx, text: txt, note: ann.note, rect });
                            }}
                        />
                    ) : hasChar ? (
                        // 有 chars 释义但无 annotation 的字：普通外观 + tooltip
                        <span
                            key={j}
                            onMouseEnter={(e) => onCharHover(char, e, e.target.getBoundingClientRect())}
                            onMouseMove={(e) => onCharHover(char, e, e.target.getBoundingClientRect())}
                            onMouseLeave={onCharLeave}
                            className="cursor-pointer underline decoration-dotted underline-offset-[3px] decoration-paper-deep/50"
                        >{char}</span>
                    ) : (
                        <span key={j}>{char}</span>
                    );
                    return pinyin ? (
                        <ruby key={j} className="ruby-text">{charNode}
                            <rt className="text-[0.45em] text-ink-faint/50 tracking-normal">{pinyin}</rt></ruby>
                    ) : charNode;
                })}
            </p>

            {popup && <AnnotationPopup popup={popup} popupRef={popupRef} onClose={closePopup} />}
        </div>
    );
}

export function SepBlock({block}) {
    return (
        <div className="flex items-center gap-4 my-10 text-paper-shadow text-xs">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-paper-deep to-transparent"/>
            {block.label}
            <div className="flex-1 h-px bg-gradient-to-r from-paper-deep via-paper-deep to-transparent"/>
        </div>
    );
}

export function CommentaryBlock({block}) {
    return (
        <div className="bg-paper-deep/30 border-l-[3px] border-gold rounded-r-md px-5 py-4 my-6">
            <p className="text-[10px] tracking-[4px] text-gold mb-2.5">{block.label}</p>
            <p className="text-sm leading-8 text-ink-light tracking-[0.05em]">{block.content}</p>
        </div>
    );
}

export function TranslationBlock({block}) {
    return (
        <div className="bg-jade/[0.06] border border-jade/20 rounded-md px-5 py-4 my-4">
            <p className="text-[10px] tracking-[4px] text-jade mb-2.5">{block.label}</p>
            <p className="text-sm leading-[1.9] text-[#3a4a3a] indent-[2em]">{block.content}</p>
        </div>
    );
}

function AnnotatedChar({char, isHovered, isPopupActive, onMouseEnter, onMouseMove, onMouseLeave, onClick}) {
    const charRef = useRef(null);

    const handleMouseEnter = (e) => {
        if (charRef.current) {
            onMouseEnter(e, charRef.current.getBoundingClientRect());
        } else {
            onMouseEnter(e, null);
        }
    };

    const handleClick = (e) => {
        const rect = charRef.current?.getBoundingClientRect() || null;
        if (onClick) onClick(e, rect);
    };

    return (
        <span
            ref={charRef}
            onMouseEnter={handleMouseEnter}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
            onClick={handleClick}
            className={`inline rounded-[2px] cursor-pointer transition-colors duration-100
        underline decoration-solid underline-offset-[3px] bg-amber-50/40
        ${isPopupActive
            ? "decoration-gold bg-gold/20 text-gold-dark"
            : isHovered
            ? "decoration-paper-deep bg-vermilion/12 text-vermilion"
            : "decoration-paper-deep/50"
        }`}>
            {char}
        </span>
    );
}

/* ── 点击弹窗组件 ── */
function AnnotationPopup({popup, popupRef, onClose}) {
    useEffect(() => {
        const handler = (e) => {
            if (popupRef.current && !popupRef.current.contains(e.target)) onClose();
        };
        document.addEventListener('mousedown', handler);
        document.addEventListener('touchstart', handler);
        return () => {
            document.removeEventListener('mousedown', handler);
            document.removeEventListener('touchstart', handler);
        };
    }, [popupRef, onClose]);

    if (!popup?.rect) return null;

    const pw = Math.min(280, window.innerWidth - 16);
    let left = popup.rect.left + popup.rect.width / 2 - pw / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - pw - 8));

    const spaceBelow = window.innerHeight - popup.rect.bottom;
    const spaceAbove = popup.rect.top;
    let top, arrowAbove;
    if (spaceBelow >= 140) { top = popup.rect.bottom + 8; arrowAbove = true; }
    else if (spaceAbove >= 140) { top = popup.rect.top - 140; arrowAbove = false; }
    else { top = Math.max(8, window.innerHeight / 2 - 70); arrowAbove = true; }

    const arrowLeft = popup.rect.left + popup.rect.width / 2 - left;

    return (
        <div ref={popupRef}
            className="fixed z-[200] bg-[#fdf6e3] border-2 border-gold/50 rounded-lg shadow-2xl animate-fade-up"
            style={{top, left, width: pw, maxWidth: 'calc(100vw - 16px)'}}>
            {arrowAbove && <>
                <div className="absolute border-x-[8px] border-x-transparent border-b-[8px] border-b-gold/50" style={{top: -8, left: Math.min(arrowLeft, pw - 16)}} />
                <div className="absolute border-x-[7px] border-x-transparent border-b-[7px] border-b-[#f7eedb]" style={{top: -6, left: Math.min(arrowLeft, pw - 16) + 1}} />
            </>}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gold/20 bg-[#f7eedb] rounded-t-lg">
                <span className="text-[15px] text-vermilion font-serif font-bold tracking-wide truncate mr-2">{popup.text}</span>
                <button onClick={onClose} className="text-ink-faint/50 hover:text-ink p-1 rounded flex-shrink-0">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
            <div className="px-4 py-3 max-h-[50vh] overflow-y-auto">
                <p className="text-sm text-ink-light leading-relaxed">{popup.note}</p>
            </div>
            {!arrowAbove && <>
                <div className="absolute border-x-[8px] border-x-transparent border-t-[8px] border-t-gold/50" style={{bottom: -8, left: Math.min(arrowLeft, pw - 16)}} />
                <div className="absolute border-x-[7px] border-x-transparent border-t-[7px] border-t-[#fdf6e3]" style={{bottom: -6, left: Math.min(arrowLeft, pw - 16) + 1}} />
            </>}
        </div>
    );
}
